import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { homeBannersAddModalStore, HomeBannersAddModalStore, ALLOWED_EXTERNAL_HOSTS, MAX_BANNER_BYTES, normalizeRequireAuthValue } from "../stores/homeBannersAddModal";
import { homeBannersStore, HomeBannersStore } from "../stores/homeBanners";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import DepositLadderBannerFields from "./DepositLadderBannerFields";
import { loadDepositLadderBannerOptions } from "../lib/depositLadderBannerOptions";

const getTodayRange = () => {
	const start = new Date();
	start.setHours(0, 0, 0, 0);
	const end = new Date();
	end.setHours(23, 59, 59, 999);

	return {
		startDate: start.toISOString(),
		endDate: end.toISOString(),
	};
};

const isAllowedExternalBannerLink = (rawValue) => {
	try {
		return ALLOWED_EXTERNAL_HOSTS.includes(rawValue);
	} catch (error) {
		return false;
	}
};

const isValidBannerFileSize = (file) => Boolean(file) && file.size <= MAX_BANNER_BYTES;

class HomeBannersAddModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			ladderOptionsLoading: false,
			ladderOptions: [],
		};
	}

	componentDidMount() {
		this.resetFormFields();
		this.loadDepositLadderOptions();
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.homeBannersAddModal.show && this.props.homeBannersAddModal.show) {
			this.resetFormFields();
			this.loadDepositLadderOptions();
			if (normalizeRequireAuthValue(this.props.homeBannersAddModal.requireAuth) !== this.props.homeBannersAddModal.requireAuth) {
				this.updateParam("requireAuth", normalizeRequireAuthValue(this.props.homeBannersAddModal.requireAuth));
			}
		}
	}

	loadDepositLadderOptions = () => {
		this.setState({ ladderOptionsLoading: true });
		loadDepositLadderBannerOptions(this.props.Interface)
			.then((ladderOptions) => this.setState({ ladderOptions, ladderOptionsLoading: false }))
			.catch((error) => {
				this.setState({ ladderOptions: [], ladderOptionsLoading: false });
				this.props.Interface.addNoty("Error", error?.text || error.toString());
			});
	};

	addBanner() {
		if (this.props.homeBannersAddModal.linkType?.value === "external" && !isAllowedExternalBannerLink(this.props.homeBannersAddModal.link)) {
			this.props.Interface.addNoty("Error", "External links are allowed only for vk or tg");

			return;
		}

		this.setState({
			loading: true,
		});
		const { requireTg, requireEmail, ...bannerPayload } = this.props.homeBannersAddModal;
		const payload = {
			...bannerPayload,
			skipLinkTg: requireTg ? 1 : 0,
			skipLinkEmail: requireEmail ? 1 : 0,
		};
		this.props.Interface.request(
			"set",
			{
				class: "data",
				module: "homeBanners",
				method: "add",
			},
			payload,
		)
			.then((result) => {
				this.setState({
					loading: false,
				});
				this.props.Interface.addNoty("Success", "Banner was successfully added");
				this.props.setHomeBannersAddModal({ ...this.props.homeBannersAddModal, show: false });
				this.props.Interface.loadHomeBannerList();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setHomeBanners({ ...this.props.homeBanners, loaded: true, error });

				this.setState({
					loading: false,
				});
			});
	}

	resetFormFields = () => {
		this.props.setHomeBannersAddModal({
			...this.props.homeBannersAddModal,
			buttonText: "",
			link: "",
			title: "",
			badge: "",
			personalization: false,
			description: "",
			descriptionType: "",
			descriptionDefault: "",
			descriptionDefaultType: "",
			analyticsID: "",
			requireAuth: "0",
			requireTg: false,
			requireEmail: false,
			showUnauthWithDepositLadder: false,
			depositLadderId: null,
			depositLadderStep: null,
			active: { value: "0", label: "0" },
			order: "",
			from: "",
			to: "",
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});
	};

	updateParam = (key, value) => {
		const updatedState = cloneDeep(this.props.homeBannersAddModal);
		updatedState[key] = value;
		this.props.setHomeBannersAddModal(updatedState);
	};

	render() {
		const { homeBannersAddModal, setHomeBannersAddModal } = this.props;
		return (
			<Modal
				visible={homeBannersAddModal.show}
				onDismiss={() => setHomeBannersAddModal({ ...homeBannersAddModal, show: false })}
				header="Add Menu Banner"
				children={
					<SpaceBetween direction="vertical" size="m">
						<Box variant="span">Title Type</Box>
						<Select filteringType="auto" placeholder="Select title type" selectedOption={homeBannersAddModal.titleType} onChange={(option) => this.updateParam("titleType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.titleType} />
						<Box variant="span">Title</Box>
						<Input
							name="Title"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Title"
							value={homeBannersAddModal.title}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									title: event.detail.value,
								})
							}
						/>
						<Box variant="span">Image</Box>
						<input
							type="file"
							accept="image/png"
							onChange={(event) => {
								const file = event.target.files[0];
								if (file) {
									if (!isValidBannerFileSize(file)) {
										this.props.Interface.addNoty("Error", `Image size must not exceed ${MAX_BANNER_BYTES / 1024 / 1024} MB`);
										event.target.value = "";

										return;
									}

									const reader = new FileReader();
									reader.onload = (event) => {
										const result = event.target.result;
										this.updateParam("image", result);
									};
									reader.readAsDataURL(file);
								} else {
									this.props.Interface.addNoty("Error", "Please, select a valid image file");
								}
							}}
						/>
						<Box variant="span">Button Text Type</Box>
						<Select filteringType="auto" placeholder="Select button type" selectedOption={homeBannersAddModal.buttonTextType} onChange={(option) => this.updateParam("buttonTextType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.buttonTextType} />
						<Box variant="span">Button Type</Box>
						<Select filteringType="auto" placeholder="Select button type" selectedOption={homeBannersAddModal.buttonType} onChange={(option) => this.updateParam("buttonType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.buttonType} />
						<Box variant="span">Button Text</Box>
						<Input
							name="ButtonText"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Button Text"
							value={homeBannersAddModal.buttonText}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									buttonText: event.detail.value,
								})
							}
						/>
						<Box variant="span">Link Type</Box>
						<Select filteringType="auto" placeholder="Select link type" selectedOption={homeBannersAddModal.linkType} onChange={(option) => this.updateParam("linkType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.linkType} />
						<Box variant="span">Link</Box>
						<Input
							name="Link"
							autoComplete="off"
							inputMode="url"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Link"
							value={homeBannersAddModal.link}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									link: event.detail.value,
								})
							}
						/>
						<Box variant="span">Description Type</Box>
						<Select filteringType="auto" placeholder="Select description type" selectedOption={homeBannersAddModal.descriptionType} onChange={(option) => this.updateParam("descriptionType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.descriptionType} />
						<Grid disableGutters gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
							<Box variant="span" margin={{ right: "xxxs" }}>
								Description
							</Box>
							<SpaceBetween direction="horizontal">
								<Box margin={{ right: "xs" }}>Personalization</Box>
								<Checkbox
									checked={homeBannersAddModal.personalization}
									onChange={({ detail }) => {
										setHomeBannersAddModal({
											...homeBannersAddModal,
											personalization: detail.checked,
										});
									}}
								/>
							</SpaceBetween>
						</Grid>

						<Input
							name="Description"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder={homeBannersAddModal.personalization ? "Description with user name key {userName}" : "Description"}
							value={homeBannersAddModal.description}
							onChange={(event) => {
								setHomeBannersAddModal({
									...homeBannersAddModal,
									description: event.detail.value,
								});
							}}
						/>

						{homeBannersAddModal.personalization && (
							<>
								<Box variant="span">Default Description Type</Box>
								<Select filteringType="auto" placeholder="Select default description type" selectedOption={homeBannersAddModal.descriptionDefaultType} onChange={(option) => this.updateParam("descriptionDefaultType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.descriptionDefaultType} />

								<Box variant="span">Default Description</Box>
								<Input
									name="descriptionDefault"
									autoComplete="off"
									inputMode="text"
									type="text"
									disableBrowserAutocorrect={true}
									placeholder="Default Description"
									value={homeBannersAddModal.descriptionDefault}
									onChange={(event) => {
										setHomeBannersAddModal({
											...homeBannersAddModal,
											descriptionDefault: event.detail.value,
										});
									}}
								/>
							</>
						)}

						<Box variant="span">Badge Type</Box>
						<Select filteringType="auto" placeholder="Select badge type" selectedOption={homeBannersAddModal.badgeType} onChange={(option) => this.updateParam("badgeType", option.detail.selectedOption)} options={homeBannersAddModal.currentOption.badgeType} />
						<Box variant="span">Badge</Box>
						<Input
							name="Badge"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Badge"
							value={homeBannersAddModal.badge}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									badge: event.detail.value,
								})
							}
						/>
						<Box variant="span">Analytics ID</Box>
						<Input
							name="AnalyticsID"
							autoComplete="off"
							inputMode="text"
							type="text"
							disableBrowserAutocorrect={true}
							placeholder="Analytics ID"
							value={homeBannersAddModal.analyticsID}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									analyticsID: event.detail.value,
								})
							}
						/>
						<SpaceBetween direction="horizontal" size="l">
							<Toggle
								checked={String(homeBannersAddModal.active?.value ?? homeBannersAddModal.active) === "1"}
								onChange={({ detail }) =>
									this.updateParam("active", {
										value: detail.checked ? "1" : "0",
										label: detail.checked ? "1" : "0",
									})
								}>
								{String(homeBannersAddModal.active?.value ?? homeBannersAddModal.active) === "1" ? "Active" : "Inactive"}
							</Toggle>
							<Toggle checked={normalizeRequireAuthValue(homeBannersAddModal.requireAuth) === "1"} onChange={({ detail }) => this.updateParam("requireAuth", detail.checked ? "1" : "0")}>
								{normalizeRequireAuthValue(homeBannersAddModal.requireAuth) === "1" ? "Required" : "Not required"}
							</Toggle>
						</SpaceBetween>
						<SpaceBetween direction="horizontal" size="l">
							<Checkbox checked={homeBannersAddModal.requireTg} onChange={({ detail }) => this.updateParam("requireTg", detail.checked)}>
								Skip link TG
							</Checkbox>
							<Checkbox checked={homeBannersAddModal.requireEmail} onChange={({ detail }) => this.updateParam("requireEmail", detail.checked)}>
								Skip link Email
							</Checkbox>
							<Checkbox checked={homeBannersAddModal.showUnauthWithDepositLadder} onChange={({ detail }) => this.updateParam("showUnauthWithDepositLadder", detail.checked)}>
								Show to guests with potential ladder
							</Checkbox>
						</SpaceBetween>
						<DepositLadderBannerFields
							ladders={this.state.ladderOptions}
							loading={this.state.ladderOptionsLoading}
							ladderId={homeBannersAddModal.depositLadderId}
							step={homeBannersAddModal.depositLadderStep}
							onChange={(depositLadderId, depositLadderStep) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									depositLadderId,
									depositLadderStep,
								})
							}
						/>
						<Box variant="span">Order</Box>
						<Input
							name="Order"
							autoComplete="off"
							inputMode="number"
							type="number"
							disableBrowserAutocorrect={true}
							placeholder="Order"
							value={homeBannersAddModal.order}
							onChange={(event) =>
								setHomeBannersAddModal({
									...homeBannersAddModal,
									order: event.detail.value,
								})
							}
						/>
						<Box variant="span">From/to</Box>
						<DateRangePicker
							onChange={({ detail }) => {
								setHomeBannersAddModal({
									...homeBannersAddModal,
									from: detail.value.startDate.split("+")[0],
									to: detail.value.endDate.split("+")[0],
									type: detail.value.type,
								});
							}}
							value={{
								startDate: homeBannersAddModal.from,
								endDate: homeBannersAddModal.to,
								type: "absolute",
							}}
							relativeOptions={[]}
							isValidRange={(range) => {
								if (range.type === "absolute") {
									const [startDateWithoutTime] = range.startDate.split("T");
									const [endDateWithoutTime] = range.endDate.split("T");
									if (!startDateWithoutTime || !endDateWithoutTime) {
										return {
											valid: false,
											errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
										};
									}
									if (new Date(range.startDate) - new Date(range.endDate) > 0) {
										return {
											valid: false,
											errorMessage: "The selected date range is invalid. The start date must be before the end date.",
										};
									}
								} else {
									return { valid: false };
								}
							}}
							i18nStrings={{
								todayAriaLabel: "Today",
								nextMonthAriaLabel: "Next month",
								previousMonthAriaLabel: "Previous month",
								customRelativeRangeDurationLabel: "Duration",
								customRelativeRangeDurationPlaceholder: "Enter duration",
								customRelativeRangeOptionLabel: "Custom range",
								customRelativeRangeOptionDescription: "Define a custom range in the past",
								customRelativeRangeUnitLabel: "Unit of time",
								formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
									const start = new Date(startDate);
									const end = new Date(endDate);

									const locale = "en-GB";
									const dateOptions = { year: "numeric", month: "long", day: "numeric" };
									const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

									const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
									const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

									return `${formattedStart} - ${formattedEnd}`;
								},
								formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
								dateTimeConstraintText: "Date and time must be within the selected range.",
								renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
								labelRelativeRange: "Relative range",
								labelAbsoluteRange: "Absolute range",
								dateTimePickerAriaLabel: "Date and time picker",
								dateTimePickerLabel: "Date and Time",
								clearButtonAriaLabel: "Clear date range",
								relativeModeTitle: "Relative mode",
								absoluteModeTitle: "Absolute mode",
								cancelButtonLabel: "Cancel",
								applyButtonLabel: "Apply",
							}}
							placeholder="From/to"
						/>
					</SpaceBetween>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button variant="normal" onClick={() => setHomeBannersAddModal({ ...homeBannersAddModal, show: false })} children="Close" />
							<Button variant="primary" disabled={this.state.loading} onClick={() => this.addBanner()} children="Ok" />
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const homeBannersAddModal = useRecoilValue(homeBannersAddModalStore);
		const setHomeBannersAddModal = useSetRecoilState(homeBannersAddModalStore);
		const homeBanners = useRecoilValue(homeBannersStore);
		const setHomeBanners = useSetRecoilState(homeBannersStore);
		return <Component {...props} homeBanners={homeBanners} setHomeBanners={setHomeBanners} setHomeBannersAddModal={setHomeBannersAddModal} homeBannersAddModal={homeBannersAddModal} />;
	};
}

export default withRecoil(HomeBannersAddModal);
